import i18n from '@/plugins/vue-i18n';
import { useAlertsStore } from '@/stores/alerts';
import { useContextStore } from '@/stores/context';
import { useSnackbarStore } from '@/stores/snackbar';
import { useWorkpackagesStore } from '@/stores/workpackages';
import to from 'await-to-js';
import axios from 'axios';
import { get, isPlainObject, size, sortBy } from 'lodash';
import { defineStore } from 'pinia';

export const useWorkpackageProductsStore = defineStore('workpackageProducts', {
  state: () => ({
    workpackageProducts: [],
    missingWorkpackageProducts: [],
    loading: false,
  }),
  getters: { missingProducts: state => sortBy(state.missingWorkpackageProducts, 'productKey') },
  actions: {
    async fetchWorkpackageProducts({ workpackageId, params = {}, options = {} }) {
      this.loading = true;
      const [err, response] = await to(
        axios.get(`/api/workpackages/${workpackageId}/products`, params)
      );
      this.loading = false;
      if (err) throw new Error(err.message);
      if (options.isStatelessFetch) return response.data;
      this.workpackageProducts = response.data;
      this.missingWorkpackageProducts = [];
    },
    setWorkpackageProducts(workpackageProducts) {
      this.workpackageProducts = workpackageProducts;
    },
    async createWorkpackageProducts({ workpackageId, products, upsert }) {
      const workpackageStore = useWorkpackagesStore();
      this.loading = true;
      if (isPlainObject(products)) {
        products = [products];
      }
      const payload = products.map(o => ({ workpackageId, ...o }));
      const method = upsert ? 'put' : 'post';

      const [err, result] = await to(
        axios[method](`/api/workpackages/${workpackageId}/products`, payload)
      );
      this.loading = false;
      if (err) {
        const message = get(err, 'response.data.message', err.message);
        throw new Error(message);
      }
      if (result.data && result.data.errors) {
        throw new Error(`Unable to create workpackage product: ${workpackageId}`);
      }
      this.setWorkpackageProducts(payload);
      workpackageStore.refreshWorkpackageInformation({ workpackageId });
      return result;
    },
    async deleteWorkpackageProducts({ workpackageId, productKeys }) {
      this.loading = true;
      const [err, result] = await to(
        axios.delete(`/api/workpackages/${workpackageId}/products`, { data: { productKeys } })
      );
      this.loading = false;
      if (err) {
        const message =
          (err && err.response && err.response.data && err.response.data.message) || err.message;
        throw new Error(message);
      }
      if (result.data && result.data.errors) {
        throw new Error(`Unable to delete products of workpackage (${workpackageId})`);
      }
      this.fetchWorkpackageProducts({ workpackageId });
      return result;
    },
    async saveWorkpackageProductSelection({
      workpackageId,
      productUpdates,
      productKeysToAdd,
      productKeysToDelete,
      categoryKeysToAdd,
      categoryKeysToDelete,
    }) {
      // add new products to the workpackage products from tooldata-products
      this.loading = true;
      const [err, result] = await to(
        axios.post(`/api/workpackages/${workpackageId}/products-selection`, {
          data: {
            productUpdates,
            productKeysToAdd,
            productKeysToDelete,
            categoryKeysToAdd,
            categoryKeysToDelete,
          },
        })
      );
      this.loading = false;
      if (err) {
        const message =
          (err && err.response && err.response.data && err.response.data.message) || err.message;
        throw new Error(message);
      }
      if (result.data && result.data.errors) {
        throw new Error(`Unable to update products of workpackage (${workpackageId})`);
      }
      return result;
    },
    async processCSV({ fileId, mappings, delimiter }) {
      const workpackageStore = useWorkpackagesStore();
      const contextStore = useContextStore();
      const snackbarStore = useSnackbarStore();
      const alertsStore = useAlertsStore();

      this.loading = true;
      const workpackageId = workpackageStore.selectedWorkpackage._id;
      const productInResetEnabled = get(
        contextStore.clientConfig,
        'features.productsInResetEnabled',
        false
      );
      const service = productInResetEnabled
        ? 'workpackage-products-inReset'
        : 'workpackage-products';
      const [err, res] = await to(
        axios.post(`/api/csv/${service}/process/${fileId}`, { mappings, delimiter, workpackageId })
      );
      this.loading = false;
      // TODO: Remove this and use global interceptor on AOV3-159
      if (err) {
        const key = get(err, 'response.data.messageKey', err.message);
        const message = i18n.t(key, get(err, 'response.data.messageParams', []));
        snackbarStore.showError(message);

        return;
      }
      const { messages, tableRows, additionalInformation } = res.data;
      if (size(tableRows)) this.workpackageProducts = tableRows;
      this.missingWorkpackageProducts = additionalInformation.missingProducts;
      if (size(messages)) {
        alertsStore.showMessages(messages);
      }
    },
  },
});
